import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  collection,
  getDocs,
  updateDoc,
  query,
  where,
  doc,
  serverTimestamp,
  setDoc,
  getDoc,
} from "firebase/firestore";

import { db } from "../firebase";

import "./AdminCommissions.css";


/* =========================================================
   CONFIG
========================================================= */

const BETS_COLLECTION = "antscore";

const USERS_COLLECTION = "users";

const COMMISSION_COLLECTION =
  "admin_commissions";


/* =========================================================
   COMMISSION PERCENTAGES
========================================================= */

const LEVEL_PERCENT = {
  level1: 8,
  level2: 6,
  level3: 4,
};


/* =========================================================
   CURRENCY MAP
========================================================= */

const CURRENCY_MAP = {
  TZS: "tshBalance",
  USDT: "usdtBalance",
  KES: "KESBalance",
  UGX: "UGXBalance",
  RWF: "RWFBalance",
  BIF: "BIFBalance",
  ZMW: "ZMWBalance",
  MWK: "MWKBalance",
  MZN: "MZNBalance",
  USD: "USDBalance",
  SSP: "SSPBalance",
  BWP: "BWPBalance",
  MGA: "MGABalance",
};


/* =========================================================
   WEEK KEY
========================================================= */

/*
  Hii inatumia Monday kama mwanzo wa week.

  Mfano:

  Monday    = Wxx
  Tuesday   = Wxx
  Wednesday = Wxx
  Thursday  = Wxx
  Friday    = Wxx

  Saturday na Sunday bado ziko kwenye
  week hiyo hiyo lakini commission
  hazihesabiwi.
*/

const getWeekKey = (
  date = new Date()
) => {

  const current =
    new Date(date);


  current.setHours(
    0,
    0,
    0,
    0
  );


  /*
    JavaScript:

    Sunday    = 0
    Monday    = 1
    Tuesday   = 2
    Wednesday = 3
    Thursday  = 4
    Friday    = 5
    Saturday  = 6
  */

  const day =
    current.getDay();


  /*
    Distance from Monday.
  */

  const daysFromMonday =
    day === 0
      ? 6
      : day - 1;


  const monday =
    new Date(current);


  monday.setDate(
    current.getDate() -
    daysFromMonday
  );


  /*
    ISO-like week number.
  */

  const year =
    monday.getFullYear();


  const firstMonday =
    new Date(
      year,
      0,
      1
    );


  const firstDay =
    firstMonday.getDay();


  const firstMondayOffset =
    firstDay === 0
      ? 1
      : firstDay === 1
        ? 0
        : 8 - firstDay;


  firstMonday.setDate(
    firstMonday.getDate() +
    firstMondayOffset
  );


  const diff =
    monday.getTime() -
    firstMonday.getTime();


  const week =
    Math.floor(
      diff /
      (
        7 *
        24 *
        60 *
        60 *
        1000
      )
    ) + 1;


  return (
    `${year}_W${week}`
  );
};


/* =========================================================
   CURRENT WEEK
========================================================= */

const getCurrentWeekKey =
  () => {

    return getWeekKey(
      new Date()
    );

  };


/* =========================================================
   PREVIOUS WEEK
========================================================= */

const getPreviousWeekKey =
  () => {

    const date =
      new Date();


    /*
      Go back exactly 7 days.
    */

    date.setDate(
      date.getDate() - 7
    );


    return getWeekKey(
      date
    );

  };


/* =========================================================
   FORMAT MONEY
========================================================= */

const formatMoney = (
  value
) => {

  return Number(
    value || 0
  ).toLocaleString(
    undefined,
    {
      minimumFractionDigits:
        2,

      maximumFractionDigits:
        2,
    }
  );

};


/* =========================================================
   ADD AMOUNT TO OBJECT
========================================================= */

const addAmountToObject = (
  object,
  currency,
  amount
) => {

  if (!currency) {
    return;
  }


  if (
    !object[currency]
  ) {

    object[currency] = 0;

  }


  object[currency] +=
    Number(
      amount || 0
    );

};


/* =========================================================
   FORMAT CURRENCY OBJECT
========================================================= */

const formatCurrency = (
  object = {}
) => {

  const entries =
    Object.entries(
      object
    ).filter(
      ([, value]) =>
        Number(value) > 0
    );


  if (
    entries.length === 0
  ) {

    return "0.00";

  }


  return entries
    .map(
      ([
        currency,
        value,
      ]) =>
        `${formatMoney(
          value
        )} ${currency}`
    )
    .join(" + ");

};


/* =========================================================
   FIRESTORE DATE PARSER
========================================================= */

const parseFirestoreDate = (
  value
) => {

  if (!value) {
    return null;
  }


  /*
    Firestore Timestamp
  */

  if (
    typeof value?.toDate ===
    "function"
  ) {

    const date =
      value.toDate();


    if (
      date instanceof Date &&
      !Number.isNaN(
        date.getTime()
      )
    ) {

      return date;

    }

  }


  /*
    Firestore Timestamp-like object
  */

  if (
    typeof value?.seconds ===
    "number"
  ) {

    const date =
      new Date(
        value.seconds *
        1000
      );


    if (
      !Number.isNaN(
        date.getTime()
      )
    ) {

      return date;

    }

  }


  /*
    JavaScript Date
  */

  if (
    value instanceof Date
  ) {

    if (
      !Number.isNaN(
        value.getTime()
      )
    ) {

      return value;

    }

  }


  /*
    Number timestamp
  */

  if (
    typeof value ===
    "number"
  ) {

    const milliseconds =
      value >
      100000000000
        ? value
        : value * 1000;


    const date =
      new Date(
        milliseconds
      );


    if (
      !Number.isNaN(
        date.getTime()
      )
    ) {

      return date;

    }

  }


  /*
    String
  */

  const parsed =
    new Date(
      value
    );


  if (
    !Number.isNaN(
      parsed.getTime()
    )
  ) {

    return parsed;

  }


  return null;

};


/* =========================================================
   CURRENT WORK WEEK
   MONDAY - FRIDAY ONLY
========================================================= */

/*
  Hii ndiyo sehemu muhimu.

  Commission inaruhusiwa:

  Monday    ✅
  Tuesday   ✅
  Wednesday ✅
  Thursday  ✅
  Friday    ✅

  Saturday  ❌
  Sunday    ❌

  Na lazima iwe current week.
*/

const isWithinCurrentWorkWeek = (
  date
) => {

  if (!date) {
    return false;
  }


  const now =
    new Date();


  /*
    Sunday = 0
    Monday = 1
    ...
    Saturday = 6
  */

  const todayDay =
    now.getDay();


  /*
    Saturday/Sunday:

    hakuna commission
  */

  if (
    todayDay === 0 ||
    todayDay === 6
  ) {

    /*
      Kama leo ni weekend,
      current work week inaishia
      Friday iliyopita.
    */

  }


  /*
    Find Monday of current week.
  */

  const monday =
    new Date(now);


  const daysFromMonday =
    todayDay === 0
      ? 6
      : todayDay - 1;


  monday.setDate(
    now.getDate() -
    daysFromMonday
  );


  monday.setHours(
    0,
    0,
    0,
    0
  );


  /*
    Friday = Monday + 4 days
  */

  const friday =
    new Date(monday);


  friday.setDate(
    monday.getDate() + 4
  );


  friday.setHours(
    23,
    59,
    59,
    999
  );


  /*
    Date must be within
    Monday-Friday.
  */

  return (
    date >= monday &&
    date <= friday
  );

};


/* =========================================================
   EMPTY RECEIVER ROW
========================================================= */

const createEmptyReceiverRow = (
  username,
  status = "waiting"
) => ({
  username,

  level1: {},

  level2: {},

  level3: {},

  total: {},

  editedTotal: {},

  previousPaid: {},

  status,
});


/* =========================================================
   ADD COMMISSION
========================================================= */

const addCommissionToMap = (
  map,
  receiverUsername,
  currency,
  amount,
  level,
  status = "waiting"
) => {

  if (
    !receiverUsername
  ) {

    return;

  }


  if (
    Number(amount || 0) <= 0
  ) {

    return;

  }


  if (
    !map[
      receiverUsername
    ]
  ) {

    map[
      receiverUsername
    ] =
      createEmptyReceiverRow(
        receiverUsername,
        status
      );

  }


  addAmountToObject(
    map[
      receiverUsername
    ][level],
    currency,
    amount
  );


  addAmountToObject(
    map[
      receiverUsername
    ].total,
    currency,
    amount
  );

};


/* =========================================================
   GET USER REFERRAL CHAIN
========================================================= */

const getUserChain = (
  usersMap,
  username
) => {

  const player =
    usersMap[
      username
    ];


  if (!player) {

    return {
      sponsor1: null,
      sponsor2: null,
      sponsor3: null,
    };

  }


  /*
    LEVEL 1
  */

  const sponsor1 =
    player.referralBy ||
    null;


  /*
    LEVEL 2
  */

  const sponsor2 =
    sponsor1 &&
    usersMap[sponsor1]
      ? (
          usersMap[
            sponsor1
          ].referralBy ||
          null
        )
      : null;


  /*
    LEVEL 3
  */

  const sponsor3 =
    sponsor2 &&
    usersMap[sponsor2]
      ? (
          usersMap[
            sponsor2
          ].referralBy ||
          null
        )
      : null;


  return {
    sponsor1,
    sponsor2,
    sponsor3,
  };

};


/* =========================================================
   COMPONENT
========================================================= */

const AdminCommission = () => {

  /* =======================================================
     ADMIN
  ======================================================= */

  const [
    adminUsername,
    setAdminUsername,
  ] = useState("");


  /* =======================================================
     CURRENT ROWS
  ======================================================= */

  const [
    rows,
    setRows,
  ] = useState([]);


  /* =======================================================
     HISTORY
  ======================================================= */

  const [
    historyRows,
    setHistoryRows,
  ] = useState([]);


  /* =======================================================
     LOADING
  ======================================================= */

  const [
    loading,
    setLoading,
  ] = useState(true);


  /* =======================================================
     ACTION LOADING
  ======================================================= */

  const [
    actionLoading,
    setActionLoading,
  ] = useState(false);


  /* =======================================================
     SEARCH
  ======================================================= */

  const [
    search,
    setSearch,
  ] = useState("");


  /* =======================================================
     MESSAGE
  ======================================================= */

  const [
    message,
    setMessage,
  ] = useState("");


  /* =======================================================
     ERROR
  ======================================================= */

  const [
    error,
    setError,
  ] = useState("");


  /* =======================================================
     WEEK KEYS
  ======================================================= */

  const currentWeekKey =
    useMemo(
      () =>
        getCurrentWeekKey(),
      []
    );


  const previousWeekKey =
    useMemo(
      () =>
        getPreviousWeekKey(),
      []
    );


  /* =======================================================
     LOAD ADMIN USERNAME
  ======================================================= */

  useEffect(
    () => {

      const username =
        localStorage.getItem(
          "username"
        ) || "";


      setAdminUsername(
        username.trim()
      );

    },
    []
  );


  /* =======================================================
     COMMISSION DOC ID
  ======================================================= */

  const commissionDocId =
    useCallback(
      (
        receiverUsername,
        weekKey =
          currentWeekKey
      ) => {

        return `${adminUsername}__${receiverUsername}__${weekKey}`;
        

      },
      [
        adminUsername,
        currentWeekKey,
      ]
    );


  /* =======================================================
     LOAD COMMISSIONS
  ======================================================= */

  const loadCommissions =
    useCallback(
      async () => {

        if (
          !adminUsername
        ) {

          setRows([]);

          setHistoryRows([]);

          setError(
            "Admin username haijapatikana kwenye localStorage."
          );

          setLoading(false);

          return;

        }


        setLoading(true);

        setError("");

        setMessage("");


        try {

          /* =============================================
             SAVED CURRENT COMMISSIONS
          ============================================= */

          const currentQuery =
            query(
              collection(
                db,
                COMMISSION_COLLECTION
              ),

              where(
                "createdByAdmin",
                "==",
                adminUsername
              ),

              where(
                "weekKey",
                "==",
                currentWeekKey
              )
            );


          const currentSnap =
            await getDocs(
              currentQuery
            );


          const savedCommissionDocs =
            {};


          currentSnap.forEach(
            (item) => {

              const data =
                item.data();


              if (
                data.username
              ) {

                savedCommissionDocs[
                  data.username
                ] = {
                  id:
                    item.id,

                  ...data,
                };

              }

            }
          );


          /* =============================================
             PREVIOUS WEEK
          ============================================= */

          const historyQuery =
            query(
              collection(
                db,
                COMMISSION_COLLECTION
              ),

              where(
                "createdByAdmin",
                "==",
                adminUsername
              ),

              where(
                "weekKey",
                "==",
                previousWeekKey
              )
            );


          const historySnap =
            await getDocs(
              historyQuery
            );


          const historyData =
            [];


          historySnap.forEach(
            (item) => {

              historyData.push({
                id:
                  item.id,

                ...item.data(),
              });

            }
          );


          /*
            Newest first.
          */

          historyData.sort(
            (
              a,
              b
            ) => {

              const dateA =
                parseFirestoreDate(
                  a.paidAt ||
                  a.updatedAt ||
                  a.createdAt
                )?.getTime() || 0;


              const dateB =
                parseFirestoreDate(
                  b.paidAt ||
                  b.updatedAt ||
                  b.createdAt
                )?.getTime() || 0;


              return (
                dateB -
                dateA
              );

            }
          );


          setHistoryRows(
            historyData
          );


          /* =============================================
             USERS
          ============================================= */

          const usersSnap =
            await getDocs(
              collection(
                db,
                USERS_COLLECTION
              )
            );


          const usersMap =
            {};


          usersSnap.forEach(
            (item) => {

              const data =
                item.data();


              if (
                data.username
              ) {

                usersMap[
                  data.username
                ] = {
                  id:
                    item.id,

                  ...data,
                };

              }

            }
          );


          /* =============================================
             ANTSCORE
             
             IMPORTANT:
             db ONLY.
             
             Hakuna standardDb.
          ============================================= */

          const betsSnap =
            await getDocs(
              collection(
                db,
                BETS_COLLECTION
              )
            );


          const commissionMap =
            {};


          /* =============================================
             PROCESS BETS
          ============================================= */

          betsSnap.forEach(
            (item) => {

              const bet =
                item.data();


              /* -----------------------------------------
                 ONLY WIN
              ----------------------------------------- */

              if (
                bet.result !==
                "win"
              ) {

                return;

              }


              /* -----------------------------------------
                 ONLY PAID OUT
              ----------------------------------------- */

              if (
                bet.paidOut !==
                true
              ) {

                return;

              }


              /* -----------------------------------------
                 PAID DATE

                 Tunatumia paidAt,
                 sio createdAt.
              ----------------------------------------- */

              const commissionDate =
                parseFirestoreDate(
                  bet.paidAt
                );


              if (
                !commissionDate
              ) {

                return;

              }


              /* -----------------------------------------
                 CURRENT WORK WEEK

                 MONDAY - FRIDAY ONLY
              ----------------------------------------- */

              if (
                !isWithinCurrentWorkWeek(
                  commissionDate
                )
              ) {

                return;

              }


              /* -----------------------------------------
                 PLAYER USERNAME
              ----------------------------------------- */

              const playerUsername =
                String(
                  bet.username ||
                  ""
                ).trim();


              if (
                !playerUsername
              ) {

                return;

              }


              /* -----------------------------------------
                 PLAYER
              ----------------------------------------- */

              const player =
                usersMap[
                  playerUsername
                ];


              if (!player) {

                return;

              }


              /* -----------------------------------------
                 CURRENCY
              ----------------------------------------- */

              const currency =
                String(
                  bet.currency ||
                  "USD"
                )
                  .trim()
                  .toUpperCase();


              /* -----------------------------------------
                 PROFIT

                 Example:

                 profit = 606

                 L1 = 90.90
                 L2 = 60.60
                 L3 = 30.30
              ----------------------------------------- */

              const profit =
                Number(
                  bet.profit ||
                  0
                );


              if (
                profit <= 0
              ) {

                return;

              }


              /* -----------------------------------------
                 REFERRAL CHAIN
              ----------------------------------------- */

              const {
                sponsor1,
                sponsor2,
                sponsor3,
              } =
                getUserChain(
                  usersMap,
                  playerUsername
                );


              /* -----------------------------------------
                 LEVEL 1 - 15%
              ----------------------------------------- */

              if (
                sponsor1
              ) {

                addCommissionToMap(
                  commissionMap,

                  sponsor1,

                  currency,

                  (
                    profit *
                    LEVEL_PERCENT.level1
                  ) / 100,

                  "level1",

                  savedCommissionDocs[
                    sponsor1
                  ]?.status ||
                  "waiting"
                );

              }


              /* -----------------------------------------
                 LEVEL 2 - 10%
              ----------------------------------------- */

              if (
                sponsor2
              ) {

                addCommissionToMap(
                  commissionMap,

                  sponsor2,

                  currency,

                  (
                    profit *
                    LEVEL_PERCENT.level2
                  ) / 100,

                  "level2",

                  savedCommissionDocs[
                    sponsor2
                  ]?.status ||
                  "waiting"
                );

              }


              /* -----------------------------------------
                 LEVEL 3 - 5%
              ----------------------------------------- */

              if (
                sponsor3
              ) {

                addCommissionToMap(
                  commissionMap,

                  sponsor3,

                  currency,

                  (
                    profit *
                    LEVEL_PERCENT.level3
                  ) / 100,

                  "level3",

                  savedCommissionDocs[
                    sponsor3
                  ]?.status ||
                  "waiting"
                );

              }

            }
          );


          /* =============================================
             FINAL ROWS
          ============================================= */

          const finalRows =
            Object.values(
              commissionMap
            ).map(
              (row) => {

                const saved =
                  savedCommissionDocs[
                    row.username
                  ];


                /*
                  No saved document.
                */

                if (
                  !saved
                ) {

                  return {

                    ...row,

                    editedTotal: {
                      ...row.total,
                    },

                    previousPaid:
                      {},

                  };

                }


                /*
                  Saved document.
                */

                return {

                  ...row,

                  status:
                    saved.status ||
                    row.status,

                  editedTotal:
                    saved.editedTotal ||
                    {
                      ...row.total,
                    },

                  previousPaid:
                    saved.previousPaid ||
                    {},

                };

              }
            );


          /*
            Sort username.
          */

          finalRows.sort(
            (
              a,
              b
            ) =>
              String(
                a.username
              ).localeCompare(
                String(
                  b.username
                )
              )
          );


          setRows(
            finalRows
          );


        } catch (
          err
        ) {

          console.error(
            "Load commission error:",
            err
          );


          setRows([]);

          setError(
            err?.message ||
            "Imeshindikana kupakia commission."
          );

        } finally {

          setLoading(false);

        }

      },
      [
        adminUsername,
        currentWeekKey,
        previousWeekKey,
      ]
    );


  /* =======================================================
     INITIAL LOAD
  ======================================================= */

  useEffect(
    () => {

      if (
        adminUsername
      ) {

        loadCommissions();

      }

    },
    [
      adminUsername,
      loadCommissions,
    ]
  );


  /* =======================================================
     CHANGE STATUS
  ======================================================= */

  const changeStatus =
    async (
      row,
      newStatus
    ) => {

      setError("");

      setMessage("");


      try {

        const commissionRef =
          doc(
            db,
            COMMISSION_COLLECTION,
            commissionDocId(
              row.username
            )
          );


        const snap =
          await getDoc(
            commissionRef
          );


        if (
          !snap.exists()
        ) {

          await setDoc(
            commissionRef,
            {

              createdByAdmin:
                adminUsername,

              username:
                row.username,

              weekKey:
                currentWeekKey,

              total:
                row.total,

              editedTotal:
                row.editedTotal ||
                row.total,

              previousPaid:
                row.previousPaid ||
                {},

              level1:
                row.level1,

              level2:
                row.level2,

              level3:
                row.level3,

              status:
                newStatus,

              createdAt:
                serverTimestamp(),

              updatedAt:
                serverTimestamp(),

            }
          );

        } else {

          await updateDoc(
            commissionRef,
            {

              status:
                newStatus,

              updatedAt:
                serverTimestamp(),

            }
          );

        }


        setRows(
          (prev) =>
            prev.map(
              (item) =>
                item.username ===
                row.username
                  ? {
                      ...item,

                      status:
                        newStatus,
                    }
                  : item
            )
        );


        setMessage(
          `${row.username} moved to ${newStatus}.`
        );


      } catch (
        err
      ) {

        console.error(
          "Status change error:",
          err
        );


        setError(
          err?.message ||
          "Imeshindikana kubadilisha status."
        );

      }

    };


  /* =======================================================
     EDIT AMOUNT
  ======================================================= */

  const handleAmountChange =
    async (
      username,
      currency,
      value
    ) => {

      const amount =
        Number(
          value || 0
        );


      /*
        Local state.
      */

      setRows(
        (prev) =>
          prev.map(
            (row) => {

              if (
                row.username !==
                username
              ) {

                return row;

              }


              return {

                ...row,

                editedTotal: {

                  ...row.editedTotal,

                  [currency]:
                    amount,

                },

              };

            }
          )
      );


      try {

        const commissionRef =
          doc(
            db,
            COMMISSION_COLLECTION,
            commissionDocId(
              username
            )
          );


        const snap =
          await getDoc(
            commissionRef
          );


        /*
          Kama document bado haipo,
          create.
        */

        if (
          !snap.exists()
        ) {

          const currentRow =
            rows.find(
              (item) =>
                item.username ===
                username
            );


          if (!currentRow) {
            return;
          }


          await setDoc(
            commissionRef,
            {

              createdByAdmin:
                adminUsername,

              username,

              weekKey:
                currentWeekKey,

              total:
                currentRow.total,

              editedTotal: {

                ...currentRow.editedTotal,

                [currency]:
                  amount,

              },

              previousPaid:
                currentRow.previousPaid ||
                {},

              level1:
                currentRow.level1,

              level2:
                currentRow.level2,

              level3:
                currentRow.level3,

              status:
                currentRow.status ||
                "processing",

              createdAt:
                serverTimestamp(),

              updatedAt:
                serverTimestamp(),

            }
          );


          return;

        }


        const oldData =
          snap.data();


        await updateDoc(
          commissionRef,
          {

            editedTotal: {

              ...(
                oldData.editedTotal ||
                oldData.total ||
                {}
              ),

              [currency]:
                amount,

            },

            updatedAt:
              serverTimestamp(),

          }
        );


      } catch (
        err
      ) {

        console.error(
          "Edit amount error:",
          err
        );


        setError(
          "Imeshindikana kuhifadhi amount."
        );

      }

    };


  /* =======================================================
     PROCESS ONE
  ======================================================= */

  const processOne =
    async (
      row
    ) => {

      setError("");

      setMessage("");


      try {

        const commissionRef =
          doc(
            db,
            COMMISSION_COLLECTION,
            commissionDocId(
              row.username
            )
          );


        const existingSnap =
          await getDoc(
            commissionRef
          );


        const existingData =
          existingSnap.exists()
            ? existingSnap.data()
            : {};


        await setDoc(
          commissionRef,
          {

            createdByAdmin:
              adminUsername,

            username:
              row.username,

            weekKey:
              currentWeekKey,

            total:
              row.total,

            editedTotal:
              row.editedTotal ||
              row.total,

            previousPaid:
              row.previousPaid ||
              {},

            level1:
              row.level1,

            level2:
              row.level2,

            level3:
              row.level3,

            status:
              "processing",

            createdAt:
              existingData.createdAt ||
              serverTimestamp(),

            updatedAt:
              serverTimestamp(),

          },

          {
            merge: true,
          }
        );


        setRows(
          (prev) =>
            prev.map(
              (item) =>
                item.username ===
                row.username
                  ? {
                      ...item,

                      status:
                        "processing",
                    }
                  : item
            )
        );


        setMessage(
          `${row.username} is now processing.`
        );


      } catch (
        err
      ) {

        console.error(
          "Process one error:",
          err
        );


        setError(
          err?.message ||
          "Process failed."
        );

      }

    };

/* =======================================================
     PAY ONE
  ======================================================= */

  const payOne =
    async (
      row
    ) => {

      setError("");

      setMessage("");


      try {

        /* =============================================
           COMMISSION DOC
        ============================================= */

        const commissionRef =
          doc(
            db,
            COMMISSION_COLLECTION,
            commissionDocId(
              row.username
            )
          );


        const commissionSnap =
          await getDoc(
            commissionRef
          );


        if (
          !commissionSnap.exists()
        ) {

          setError(
            "Commission document haipo."
          );

          return;

        }


        const commissionData =
          commissionSnap.data();


        /* =============================================
           MUST BE PROCESSING
        ============================================= */

        if (
          commissionData.status !==
          "processing"
        ) {

          setError(
            "Commission lazima iwe processing kwanza."
          );

          return;

        }


        /* =============================================
           FIND USER
        ============================================= */

        const usersQuery =
          query(
            collection(
              db,
              USERS_COLLECTION
            ),

            where(
              "username",
              "==",
              row.username
            )
          );


        const userSnap =
          await getDocs(
            usersQuery
          );


        if (
          userSnap.empty
        ) {

          setError(
            `User ${row.username} hajapatikana.`
          );

          return;

        }


        const userDoc =
          userSnap.docs[0];


        const userData =
          userDoc.data();


        /* =============================================
           BALANCE UPDATES
        ============================================= */

        const updates =
          {};


        Object.entries(
          commissionData.editedTotal ||
          {}
        ).forEach(
          ([
            currency,
            amount,
          ]) => {

            const field =
              CURRENCY_MAP[
                currency
              ];


            if (!field) {
              return;
            }


            const commissionAmount =
              Number(
                amount || 0
              );


            if (
              commissionAmount <=
              0
            ) {

              return;

            }


            const oldBalance =
              Number(
                userData[
                  field
                ] || 0
              );


            updates[field] =
              oldBalance +
              commissionAmount;

          }
        );


        /* =============================================
           NOTHING TO PAY
        ============================================= */

        if (
          Object.keys(
            updates
          ).length === 0
        ) {

          setError(
            "Hakuna commission amount ya kulipa."
          );

          return;

        }


        /* =============================================
           UPDATE USER BALANCE
        ============================================= */

        await updateDoc(
          doc(
            db,
            USERS_COLLECTION,
            userDoc.id
          ),
          updates
        );


        /* =============================================
           MARK SUCCESS
        ============================================= */

        await updateDoc(
          commissionRef,
          {

            status:
              "success",

            previousPaid:
              commissionData.editedTotal ||
              {},

            editedTotal:
              commissionData.editedTotal ||
              {},

            paidAt:
              serverTimestamp(),

            updatedAt:
              serverTimestamp(),

          }
        );


        /* =============================================
           LOCAL STATE
        ============================================= */

        setRows(
          (prev) =>
            prev.map(
              (item) =>
                item.username ===
                row.username
                  ? {

                      ...item,

                      status:
                        "success",

                      previousPaid:
                        commissionData.editedTotal ||
                        {},

                      editedTotal:
                        commissionData.editedTotal ||
                        {},

                    }
                  : item
            )
        );


        setMessage(
          `Commission ya ${row.username} imelipwa successfully.`
        );


      } catch (
        err
      ) {

        console.error(
          "Pay one error:",
          err
        );


        setError(
          err?.message ||
          "Pay failed."
        );

      }

    };


  /* =======================================================
     PROCESS ALL
  ======================================================= */

  const moveToProcessing =
    async () => {

      setActionLoading(true);

      setError("");

      setMessage("");


      try {

        const waitingRows =
          rows.filter(
            (row) =>
              row.status ===
              "waiting"
          );


        for (
          const row
          of waitingRows
        ) {

          await processOne(
            row
          );

        }


        setMessage(
          `${waitingRows.length} commission(s) moved to processing.`
        );


      } catch (
        err
      ) {

        console.error(
          "Process all error:",
          err
        );


        setError(
          err?.message ||
          "Process all failed."
        );

      } finally {

        setActionLoading(false);

      }

    };


  /* =======================================================
     PAY ALL
  ======================================================= */

  const moveToSuccess =
    async () => {

      setActionLoading(true);

      setError("");

      setMessage("");


      try {

        const processingRows =
          rows.filter(
            (row) =>
              row.status ===
              "processing"
          );


        for (
          const row
          of processingRows
        ) {

          await payOne(
            row
          );

        }


        setMessage(
          `${processingRows.length} commission(s) processed for payment.`
        );


      } catch (
        err
      ) {

        console.error(
          "Pay all error:",
          err
        );


        setError(
          err?.message ||
          "Pay all failed."
        );

      } finally {

        setActionLoading(false);

      }

    };

    
    
  /* =======================================================
     SEARCH
  ======================================================= */

  const filteredRows =
    useMemo(
      () => {

        const term =
          search
            .trim()
            .toLowerCase();


        if (!term) {

          return rows;

        }


        return rows.filter(
          (row) =>
            String(
              row.username ||
              ""
            )
              .toLowerCase()
              .includes(
                term
              )
        );

      },
      [
        rows,
        search,
      ]
    );

    const waitingRows = useMemo(
      () =>
        filteredRows.filter(
          (row) => row.status === "waiting"
        ),
      [filteredRows]
    );
    
    const processingRows = useMemo(
      () =>
        filteredRows.filter(
          (row) => row.status === "processing"
        ),
      [filteredRows]
    );
    
    const successRows = useMemo(
      () =>
        filteredRows.filter(
          (row) => row.status === "success"
        ),
      [filteredRows]
    );
  /* =======================================================
     SUMMARY
  ======================================================= */

  const summary =
    useMemo(
      () => {

        return {

          total:
            rows.length,

          waiting:
            rows.filter(
              (row) =>
                row.status ===
                "waiting"
            ).length,

          processing:
            rows.filter(
              (row) =>
                row.status ===
                "processing"
            ).length,

          success:
            rows.filter(
              (row) =>
                row.status ===
                "success"
            ).length,

        };

      },
      [
        rows,
      ]
    );


  /* =======================================================
     STATUS CLASS
  ======================================================= */

  const statusClass =
    (status) => {

      if (
        status ===
        "success"
      ) {

        return "status-success";

      }


      if (
        status ===
        "processing"
      ) {

        return "status-processing";

      }


      return "status-waiting";

    };


  /* =======================================================
     RENDER COMMISSION CARD
  ======================================================= */

  const renderCommissionCard =
    (
      row,
      index
    ) => {

      const currencies =
        Object.keys(
          row.editedTotal ||
          row.total ||
          {}
        );


      return (

        <div
          className="commission-card"
          key={
            `${row.username}-${index}`
          }
        >

          {/* =============================================
              TOP
          ============================================= */}

          <div
            className="commission-card-top"
          >

            <div
              className="commission-user"
            >

              <div
                className="user-avatar"
              >

                {
                  row.username
                    ?.charAt(
                      0
                    )
                    ?.toUpperCase() ||
                  "U"
                }

              </div>


              <div>

                <span>
                  COMMISSION RECEIVER
                </span>

                <h3>
                  {row.username}
                </h3>

              </div>

            </div>


            <div
              className={
                `commission-status  `+
                statusClass(
                  row.status
                )
              }
            >

              <i />

              {row.status}

            </div>

          </div>


          {/* =============================================
              LEVELS
          ============================================= */}

          <div
            className="commission-levels"
          >

            <div
              className={
                "level-box level-one"
              }
            >

              <span>
                LEVEL 1
              </span>

              <b>
                8%
              </b>

              <strong>
                {formatCurrency(
                  row.level1
                )}
              </strong>

            </div>


            <div
              className={
                "level-box level-two"
              }
            >

              <span>
                LEVEL 2
              </span>

              <b>
                6%
              </b>

              <strong>
                {formatCurrency(
                  row.level2
                )}
              </strong>

            </div>


            <div
              className={
                "level-box level-three"
              }
            >

              <span>
                LEVEL 3
              </span>

              <b>
                4%
              </b>

              <strong>
                {formatCurrency(
                  row.level3
                )}
              </strong>

            </div>

          </div>


          {/* =============================================
              TOTAL
          ============================================= */}

          <div
            className="commission-total"
          >

            <div>

              <span>
                ORIGINAL COMMISSION
              </span>

              <strong>
                {formatCurrency(
                  row.total
                )}
              </strong>

            </div>


            <div>

              <span>
                PREVIOUSLY PAID
              </span>

              <strong>
                {formatCurrency(
                  row.previousPaid
                )}
              </strong>

            </div>

          </div>


          {/* =============================================
              EDIT
          ============================================= */}

          <div
            className="commission-edit"
          >

            <div
              className="edit-heading"
            >

              <div>

                <span>
                  PAYMENT AMOUNT
                </span>

                <strong>
                  Edited Commission
                </strong>

              </div>


              <small>

                {
                  row.status ===
                  "processing"
                    ? "EDITABLE"
                    : "LOCKED"
                }

              </small>

            </div>


            <div
              className="currency-edit-list"
            >

              {
                currencies.length ===
                0 ? (

                  <div
                    className="no-amount"
                  >
                    No commission
                  </div>

                ) : (

                  currencies.map(
                    (
                      currency
                    ) => (

                      <div
                        className="currency-edit"
                        key={
                          currency
                        }
                      >

                        <span>
                          {currency}
                        </span>


                        {
                          row.status ===
                          "processing" ? (

                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={
                                row
                                  .editedTotal?.[
                                  currency
                                ] ?? 0
                              }
                              onChange={
                                (
                                  event
                                ) =>
                                  handleAmountChange(
                                    row.username,
                                    currency,
                                    event
                                      .target
                                      .value
                                  )
                              }
                            />

                          ) : (

                            <strong>
                              {formatMoney(
                                row
                                  .editedTotal?.[
                                  currency
                                ]
                              )}
                            </strong>

                          )
                        }

                      </div>

                    )
                  )

                )
              }

            </div>

          </div>


          {/* =============================================
              ACTIONS
          ============================================= */}

          <div
            className="commission-actions"
          >

            {
              row.status ===
              "waiting" && (

                <button
                  className="btn-process"
                  disabled={
                    actionLoading
                  }
                  onClick={() =>
                    processOne(
                      row
                    )
                  }
                >
                  🔄 Process
                </button>

              )
            }


            {
              row.status ===
              "processing" && (

                <>

                  <button
                    className="btn-pay"
                    disabled={
                      actionLoading
                    }
                    onClick={() =>
                      payOne(
                        row
                      )
                    }
                  >
                    ✓ Pay Now
                  </button>


                  <button
                    className="btn-waiting"
                    disabled={
                      actionLoading
                    }
                    onClick={() =>
                      changeStatus(
                        row,
                        "waiting"
                      )
                    }
                  >
                    ← Waiting
                  </button>

                </>

              )
            }


            {
              row.status ===
              "success" && (

                <>

                  <div
                    className="paid-label"
                  >
                    ✓ PAID
                  </div>


                  <button
                    className="btn-processing"
                    disabled={
                      actionLoading
                    }
                    onClick={() =>
                      changeStatus(
                        row,
                        "processing"
                      )
                    }
                  >
                    Processing
                  </button>


                  <button
                    className="btn-waiting"
                    disabled={
                      actionLoading
                    }
                    onClick={() =>
                      changeStatus(
                        row,
                        "waiting"
                      )
                    }
                  >
                    Waiting
                  </button>

                </>

              )
            }

          </div>

        </div>

      );

    };


  /* =======================================================
     LOADING
  ======================================================= */

  if (
    loading
  ) {

    return (

      <div
        className="admin-page"
      >

        <div
          className="admin-loading"
        >

          <div
            className="admin-loader"
          >

            <span />
            <span />
            <span />

          </div>


          <p>
            Loading commission dashboard...
          </p>

        </div>

      </div>

    );

  }


  /* =======================================================
     UI
  ======================================================= */

  return (

    <div
      className="admin-page"
    >

      <div
        className="admin-container"
      >

        {/* ===============================================
            HEADER
        =============================================== */}

        <header
          className="admin-header"
        >

          <div>

            <div
              className="admin-badge"
            >
              🔥 ADMIN CONTROL CENTER
            </div>


            <h1>
              Commission
              <span>
                Dashboard
              </span>
            </h1>


            <p>
              Manage referral commissions,
              review payments and update
              user balances.
            </p>

          </div>


          <div
            className="admin-week"
          >

            <span>
              CURRENT WORK WEEK
            </span>


            <strong>
              {currentWeekKey}
            </strong>


            <small>
              Monday - Friday
            </small>

          </div>

        </header>


        {/* ===============================================
            ALERT MESSAGE
        =============================================== */}

        {
          message && (

            <div
              className="admin-message"
            >

              <span>
                ✓
              </span>


              {message}


              <button
                onClick={() =>
                  setMessage("")
                }
              >
                ×
              </button>

            </div>

          )
        }


        {/* ===============================================
            ERROR
        =============================================== */}

        {
          error && (

            <div
              className="admin-error"
            >

              <span>
                ⚠️
              </span>


              {error}


              <button
                onClick={() =>
                  setError("")
                }
              >
                ×
              </button>

            </div>

          )
        }


        {/* ===============================================
            STATS
        =============================================== */}

        <div
          className="admin-stats"
        >

          <div
            className="stat-card"
          >

            <span>
              TOTAL USERS
            </span>

            <strong>
              {summary.total}
            </strong>

            <small>
              Commission receivers
            </small>

          </div>


          <div
            className={
              "stat-card waiting-stat"
            }
          >

            <span>
              WAITING
            </span>

            <strong>
              {summary.waiting}
            </strong>

            <small>
              Awaiting processing
            </small>

          </div>


          <div
            className={
              "stat-card processing-stat"
            }
          >

            <span>
              PROCESSING
            </span>

            <strong>
              {summary.processing}
            </strong>

            <small>
              Ready for payment
            </small>

          </div>


          <div
            className={
              "stat-card success-stat"
            }
          >

            <span>
              SUCCESS
            </span>

            <strong>
              {summary.success}
            </strong>

            <small>
              Successfully paid
            </small>

          </div>

        </div>


        {/* ===============================================
            TOOLBAR
        =============================================== */}

        <div
          className="admin-toolbar"
        >

          <div
            className="admin-search"
          >

            <span>
              ⌕
            </span>


            <input
              type="text"
              placeholder="Search username..."
              value={
                search
              }
              onChange={
                (event) =>
                  setSearch(
                    event
                      .target
                      .value
                  )
              }
            />


            {
              search && (

                <button
                  onClick={() =>
                    setSearch("")
                  }
                >
                  ×
                </button>

              )
            }

          </div>


          <div
            className="admin-actions"
          >

            <button
              className="btn-refresh"
              onClick={
                loadCommissions
              }
              disabled={
                actionLoading
              }
            >
              ↻ Refresh
            </button>


            <button
              className="btn-process-all"
              onClick={
                moveToProcessing
              }
              disabled={
                actionLoading ||
                summary.waiting ===
                  0
              }
            >
              🔄 Process All
            </button>


            <button
              className="btn-pay-all"
              onClick={
                moveToSuccess
              }
              disabled={
                actionLoading ||
                summary.processing ===
                  0
              }
            >
              ✓ Pay All
            </button>

          </div>

        </div>


        {/* ===============================================
            CURRENT COMMISSION
        =============================================== */}

        
        {/* =====================================================
    WAITING
===================================================== */}

<section className="commission-section status-section">

<div className="section-title">

  <div>
    <span>WAITING</span>

    <h2>
      Commission Queue
    </h2>

    <small>
      Commissions awaiting processing
    </small>
  </div>

  <div className="section-count waiting-count">
    {waitingRows.length}
  </div>

</div>


{waitingRows.length === 0 ? (

  <div className="admin-empty compact-empty">

    <div>⏳</div>

    <h3>
      No Waiting Commission
    </h3>

    <p>
      Hakuna commission inayosubiri processing.
    </p>

  </div>

) : (

  <div className="admin-commission-table-wrapper">

    <table className="admin-commission-table">

      <thead>
        <tr>
          <th>USERNAME</th>
          <th>LEVEL 1</th>
          <th>LEVEL 2</th>
          <th>LEVEL 3</th>
          <th>TOTAL</th>
          <th>PAYMENT</th>
          <th>STATUS</th>
          <th>ACTION</th>
        </tr>
      </thead>

      <tbody>

        {waitingRows.map((row) => {

          const currencies =
            Object.keys(
              row.editedTotal ||
              row.total ||
              {}
            );

          return (

            <tr key={row.username}>

              <td>
                <strong>
                  {row.username}
                </strong>
              </td>

              <td>
                {formatCurrency(row.level1)}
              </td>

              <td>
                {formatCurrency(row.level2)}
              </td>

              <td>
                {formatCurrency(row.level3)}
              </td>

              <td>
                <strong>
                  {formatCurrency(row.total)}
                </strong>
              </td>

              <td>

                <div className="table-payment-list">

                  {currencies.map((currency) => (

                    <div
                      className="table-payment-item"
                      key={currency}
                    >

                      <span>
                        {currency}
                      </span>

                      <strong>
                        {formatMoney(
                          row.editedTotal?.[currency]
                        )}
                      </strong>

                    </div>

                  ))}

                </div>

              </td>

              <td>

                <div className="commission-status status-waiting">

                  <i />

                  waiting

                </div>

              </td>

              <td>

                <div className="table-actions">

                  <button
                    className="btn-process"
                    disabled={actionLoading}
                    onClick={() =>
                      processOne(row)
                    }
                  >
                    🔄 Process
                  </button>

                </div>

              </td>

            </tr>

          );

        })}

      </tbody>

    </table>

  </div>

)}

</section>


{/* =====================================================
  PROCESSING
===================================================== */}

<section className="commission-section status-section processing-section">

<div className="section-title">

  <div>
    <span>PROCESSING</span>

    <h2>
      Processing Queue
    </h2>

    <small>
      Commissions ready for payment
    </small>
  </div>

  <div className="section-count processing-count">
    {processingRows.length}
  </div>

</div>


{processingRows.length === 0 ? (

  <div className="admin-empty compact-empty">

    <div>🔄</div>

    <h3>
      No Processing Commission
    </h3>

    <p>
      Hakuna commission iliyopo kwenye processing.
    </p>

  </div>

) : (

  <div className="admin-commission-table-wrapper">

    <table className="admin-commission-table">

      <thead>
        <tr>
          <th>USERNAME</th>
          <th>LEVEL 1</th>
          <th>LEVEL 2</th>
          <th>LEVEL 3</th>
          <th>TOTAL</th>
          <th>PAYMENT</th>
          <th>STATUS</th>
          <th>ACTION</th>
        </tr>
      </thead>

      <tbody>

        {processingRows.map((row) => {

          const currencies =
            Object.keys(
              row.editedTotal ||
              row.total ||
              {}
            );

          return (

            <tr key={row.username}>

              <td>
                <strong>
                  {row.username}
                </strong>
              </td>

              <td>
                {formatCurrency(row.level1)}
              </td>

              <td>
                {formatCurrency(row.level2)}
              </td>

              <td>
                {formatCurrency(row.level3)}
              </td>

              <td>
                <strong>
                  {formatCurrency(row.total)}
                </strong>
              </td>

              <td>

                <div className="table-payment-list">

                  {currencies.map((currency) => (

                    <div
                      className="table-payment-item"
                      key={currency}
                    >

                      <span>
                        {currency}
                      </span>

                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={
                          row.editedTotal?.[currency] ?? 0
                        }
                        onChange={(event) =>
                          handleAmountChange(
                            row.username,
                            currency,
                            event.target.value
                          )
                        }
                      />

                    </div>

                  ))}

                </div>

              </td>

              <td>

                <div className="commission-status status-processing">

                  <i />

                  processing

                </div>

              </td>

              <td>

                <div className="table-actions">

                  <button
                    className="btn-pay"
                    disabled={actionLoading}
                    onClick={() =>
                      payOne(row)
                    }
                  >
                    ✓ Pay
                  </button>

                  <button
                    className="btn-waiting"
                    disabled={actionLoading}
                    onClick={() =>
                      changeStatus(
                        row,
                        "waiting"
                      )
                    }
                  >
                    ← Waiting
                  </button>

                </div>

              </td>

            </tr>

          );

        })}

      </tbody>

    </table>

  </div>

)}

</section>


{/* =====================================================
  SUCCESS
===================================================== */}

<section className="commission-section status-section success-section">

<div className="section-title">

  <div>
    <span>SUCCESS</span>

    <h2>
      Successfully Paid
    </h2>

    <small>
      Commissions already paid to users
    </small>
  </div>

  <div className="section-count success-count">
    {successRows.length}
  </div>

</div>


{successRows.length === 0 ? (

  <div className="admin-empty compact-empty">

    <div>✓</div>

    <h3>
      No Paid Commission
    </h3>

    <p>
      Hakuna commission iliyolipwa bado.
    </p>

  </div>

) : (

  <div className="admin-commission-table-wrapper">

    <table className="admin-commission-table">

      <thead>
        <tr>
          <th>USERNAME</th>
          <th>LEVEL 1</th>
          <th>LEVEL 2</th>
          <th>LEVEL 3</th>
          <th>TOTAL</th>
          <th>PAID AMOUNT</th>
          <th>STATUS</th>
          <th>ACTION</th>
        </tr>
      </thead>

      <tbody>

        {successRows.map((row) => {

          const paidAmount =
            row.previousPaid ||
            row.editedTotal ||
            row.total ||
            {};

          return (

            <tr key={row.username}>

              <td>
                <strong>
                  {row.username}
                </strong>
              </td>

              <td>
                {formatCurrency(row.level1)}
              </td>

              <td>
                {formatCurrency(row.level2)}
              </td>

              <td>
                {formatCurrency(row.level3)}
              </td>

              <td>
                <strong>
                  {formatCurrency(row.total)}
                </strong>
              </td>

              <td>

                <div className="table-paid-amount">

                  {Object.entries(
                    paidAmount
                  ).map(
                    ([currency, amount]) => (

                      <div
                        key={currency}
                        className="table-payment-item"
                      >

                        <span>
                          {currency}
                        </span>

                        <strong>
                          {formatMoney(amount)}
                        </strong>

                      </div>

                    )
                  )}

                </div>

              </td>

              <td>

                <div className="commission-status status-success">

                  <i />

                  success

                </div>

              </td>

              <td>

                <div className="table-actions">

                  <div className="paid-label">
                    ✓ PAID
                  </div>

                  <button
                    className="btn-processing"
                    disabled={actionLoading}
                    onClick={() =>
                      changeStatus(
                        row,
                        "processing"
                      )
                    }
                  >
                    Processing
                  </button>

                  <button
                    className="btn-waiting"
                    disabled={actionLoading}
                    onClick={() =>
                      changeStatus(
                        row,
                        "waiting"
                      )
                    }
                  >
                    Waiting
                  </button>

                </div>

              </td>

            </tr>

          );

        })}

      </tbody>

    </table>

  </div>

)}

</section>

      </div>

    </div>

  );

};


export default AdminCommission;

